import * as L from 'leaflet';
import { ProviderCardActions, VesselCardActions } from '../contracts/vessel.types';
import { AisMapContext, PopupPort } from '../contracts/ais-map.context';

/**
 * What this module must be TOLD rather than know (rule R5 / phase-4
 * boundary). Two kinds of seam, both temporary in shape:
 *
 *  - the CARD BUILDERS, because the card markup lives with the provider today
 *    (`voyage-vessel-popup.ts` — phase 8 moves it shared, at which point these
 *    become direct imports);
 *  - the ACTION DISPATCH, which is now ONE callback rather than four named
 *    ones (D15). This module used to close over `.ais-popup__ports` and
 *    `.ais-popup__nearby` and call `ctx.host.portCalls` / `ctx.host.nearby`
 *    directly — shared core dispatching two of one provider's features by
 *    name. It reads `data-card-action` and hands the string back instead, so
 *    a provider adds a card button without this file learning its name.
 */
export interface PopupHostOptions {
  /** The standard vessel card's markup for one hull. */
  buildVesselCard: (properties: any, actions: VesselCardActions) => any;
  /** The cluster card's markup — the name-your-hulls list. */
  buildClusterCard: (vessels: Array<{ name?: any; mmsi?: any }>) => any;
  /**
   * The provider's OWN card keys for one hull — its mounted features'
   * `popupActions(mmsi)` merged, plus anything its facade adds (its lookup
   * price, for instance, which used to be a named option here). A feature that
   * is absent contributes nothing; one whose drawer is unmounted contributes
   * null, which hides its button (invariant I5).
   *
   * Never inspected. Merged UNDER the two keys this host owns and passed to
   * the card builder as-is.
   */
  providerActions: (mmsi: string) => ProviderCardActions;
  /** The hull has a saved record, drawn or not (located store). */
  isSaved: (mmsi: string) => boolean;
  /** The hull's located marker is currently on the map (located store). */
  isLocatedOnMap: (mmsi: string) => boolean;
  /**
   * A card button carrying `data-card-action` was clicked — the provider
   * routes the key to whichever controller owns it.
   *
   * The one seam that replaced four. Everything the shared host still handles
   * itself (`save`, `offmap`, `close`) is answered from shared map-core state;
   * everything else is a string it does not interpret.
   */
  dispatchCardAction: (action: string, mmsi: string) => void;
  /** The card's free save (located store). */
  saveVessel: (mmsi: string) => void;
  /** Take a located marker off the map, record kept (located store). */
  takeOffMap: (mmsi: string) => void;
}

/**
 * THE VESSEL CARD's host — the click delegate that makes its buttons work,
 * the popupActions merge that decides which buttons exist, and the one open
 * path every layer shares. Extracted verbatim from `VoyageMapController`
 * (phase 4, REFACTOR-PLAN.md §7); peers are reached through `ctx` ports only
 * (rule R2).
 *
 * Leaflet popups render outside Angular, so a template binding can never
 * reach them — everything here exists because the card is plain DOM and this
 * module is its only bridge back into the application.
 */
export class PopupHost implements PopupPort {
  /**
   * The vessel popup currently open and the hull it describes. Maintained by
   * the `popupopen`/`popupclose` listeners in `ensurePopupActionsWired`, which
   * read the card's own `data-mmsi` — so it holds whichever of the four layers
   * opened the card, not just this module's own.
   *
   * Two things need it. The take-off paths close the card of the vessel they
   * remove (a card opened at a point rather than bound to a marker does not
   * leave with that marker, so it was left floating over empty water), and the
   * lookup marks the card of the vessel it is fetching as updating. Both are
   * scoped by MMSI: an unrelated card must neither close nor spin.
   *
   * Only one is ever open — opening closes the previous — so a single reference
   * is the whole of this state.
   */
  private _vesselPopup: any = null;
  private _vesselPopupMmsi: string | null = null;
  /** The map container carrying the popup click-delegate (rewired if it changes). */
  private _popupClickTarget: any = null;

  constructor(
    private readonly ctx: AisMapContext,
    private readonly opts: PopupHostOptions,
  ) {}

  /**
   * The popup context-menu for a hull, resolved from its state. Every popup
   * gets the paid lookup and the free save toggle; a located (amber)
   * marker additionally gets Remove-from-the-map.
   *
   * The two used to be one button: a located marker offered only "Remove from
   * the map", and that also deleted the saved record. So the menu could
   * delete a saved vessel while saying it was tidying your map, and
   * there was no way to do either one on its own. They are now what they say —
   * the toggle owns Saved Vessels, remove owns the map.
   *
   * THE FEATURE BUTTONS ARE THE FEATURES' OWN (phase 5, §4.3): port calls and
   * nearby each contribute their key through `popupActions()`, gated on their
   * drawer being mounted — the button does nothing but open a drawer, and
   * `openAndFetch` returns immediately when `onRequestOpen` is null, so an
   * unmounted drawer contributes a null price and the button never renders
   * (invariant I5). Closing a replay restores the live bar and the buttons
   * with it. This method's own keys are the card's basics: the paid lookup,
   * the free save, and the located marker's take-off.
   */
  popupOptsFor(mmsi: string): VesselCardActions {
    const m = String(mmsi ?? '').trim();
    if (!m) return { save: null };
    return {
      // The PROVIDER's keys first, so the two this host owns below cannot be
      // overwritten by one. The merge is last-wins, and a provider returning a
      // key it does not own would otherwise silently replace core's answer —
      // `lookupCredits` used to sit above this spread and was the one basic a
      // feature could clobber, which `popup.host.spec.ts` caught on its first
      // run. That guard is now narrower AND stronger: only two keys need
      // protecting, and they are the only two this file understands.
      ...this.opts.providerActions(m),
      // Offered only while the hull is NOT saved. DELETING the record is the
      // Saved Vessels section's job — see the card builder.
      save: this.opts.isSaved(m) ? null : 'add',
      // Only a located marker can be taken off on its own; snapshot hulls leave
      // by the type filter or with their layer.
      removeFromMap: this.opts.isLocatedOnMap(m),
    };
  }

  /**
   * One click-delegate on the map container for the popup's header actions —
   * Leaflet popups render outside Angular, so a template binding can never
   * reach them. Idempotent per container; rewired if the map is rebuilt.
   *
   * The free add closes the card, because its own button is what disappears on
   * the next open and a button that has just become a lie is worse than none.
   * The paid update keeps it open until the fresh card replaces it.
   */
  ensurePopupActionsWired(): void {
    const container = this.ctx.host.map?.getContainer?.();
    if (!container || this._popupClickTarget === container) return;
    this._popupClickTarget = container;

    // Tracking, not actions — but it belongs to the same wiring, and for the
    // same reason: FOUR layers open this card (the live and history layers bind
    // their own popups in the host, playback builds one per click, and only
    // `openVesselPopupAt` is this module's), so tracking it at the point of
    // creation would mean four places to keep in step. One `popupopen` listener
    // sees them all. Tracking only the one path is what made the update spinner
    // appear on the SECOND press: the first card was one this module had
    // never seen, and the second — reopened by the lookup itself — was ours.
    const map = this.ctx.host.map;
    map?.on?.('popupopen', (e: any) => {
      // `[data-mmsi]` is what makes this a vessel card: a cluster popup carries
      // the same class and no hull.
      const card = e?.popup?.getElement?.()?.querySelector?.('.ais-popup[data-mmsi]');
      this._vesselPopup = card ? e.popup : null;
      this._vesselPopupMmsi = card?.getAttribute?.('data-mmsi') || null;
    });
    map?.on?.('popupclose', (e: any) => {
      if (e?.popup !== this._vesselPopup) return;
      this._vesselPopup = null;
      this._vesselPopupMmsi = null;
    });
    container.addEventListener('click', (e: any) => {
      const target = e?.target;
      // THE PROVIDER'S BUTTONS — one branch for all of them (D15).
      //
      // Four named branches stood here, two of which called
      // `ctx.host.portCalls` / `ctx.host.nearby` directly: shared core
      // dispatching one provider's features by name. Now the button carries
      // its own key and this file hands the string back without reading it.
      //
      // The MMSI comes off the CARD, not from the map's selection, and that is
      // load-bearing for every key: four layers open this popup and only the
      // playback one calls `activateVessel`, so on a live snapshot hull there
      // is no active vessel to read — the port-call button used to open an
      // empty drawer and buy nothing on exactly that path. The card is the one
      // thing that always knows whose it is.
      const action = target?.closest?.('[data-card-action]');
      if (action) {
        const key = action.getAttribute?.('data-card-action');
        const mmsi = action.getAttribute?.('data-mmsi');
        if (key && mmsi) this.opts.dispatchCardAction(String(key), String(mmsi));
        return;
      }
      const save = target?.closest?.('.ais-popup__save');
      if (save) {
        const mmsi = save.getAttribute?.('data-mmsi');
        if (!mmsi) return;
        this.ctx.host.map?.closePopup?.();
        this.opts.saveVessel(String(mmsi));
        return;
      }
      const offmap = target?.closest?.('.ais-popup__offmap');
      if (offmap) {
        const mmsi = offmap.getAttribute?.('data-mmsi');
        if (!mmsi) return;
        // Closes the card itself: the marker it is anchored to is going, and
        // the take-off path already owns that (see `closeVesselPopupFor`).
        this.opts.takeOffMap(String(mmsi));
        return;
      }
      if (target?.closest?.('.ais-popup__close')) this.ctx.host.map?.closePopup?.();
    });
  }

  /** One place that opens the standard vessel popup at a point. */
  openVesselPopupAt(latlng: any, properties: any, opts?: VesselCardActions): void {
    if (!this.ctx.host.map || !latlng) return;
    this.ensurePopupActionsWired();
    const mmsi = String(properties?.['MMSI'] ?? properties?.mmsi ?? '').trim();
    // The card prints the hull's position, and the point this popup is anchored
    // at IS that position — so a caller that hands over sparse properties (a
    // search hit is name + MMSI and nothing else) still gets a truthful one,
    // rather than the row silently vanishing on some popups and not others.
    const withPosition =
      properties && (properties['Lat'] ?? properties.lat ?? properties.latitude) != null
        ? properties
        : { ...(properties ?? {}), Lat: latlng.lat, Lng: latlng.lng };
    // Not tracked here: `ensurePopupActionsWired`'s `popupopen` listener records
    // it, the same way it records the three cards this method does not open.
    L.popup({
      className: 'ais-vessel-popup',
      closeButton: false,
      offset: L.point(0, -6),
      autoPanPadding: L.point(40, 90),
    } as any)
      .setLatLng(latlng)
      .setContent(this.opts.buildVesselCard(withPosition, opts ?? this.popupOptsFor(mmsi)))
      .openOn(this.ctx.host.map);
  }

  /**
   * The cluster card at a point — the name-your-hulls answer a count bubble
   * gives when zooming cannot separate its members (see `onClusterClick`).
   * Same popup chrome as the vessel card, no hull, so the `popupopen`
   * listener above correctly declines to track it.
   */
  openClusterPopupAt(latlng: any, vessels: Array<{ name?: any; mmsi?: any }>): void {
    L.popup({
      className: 'ais-vessel-popup',
      closeButton: false,
      offset: L.point(0, -6),
      autoPanPadding: L.point(40, 90),
    } as any)
      .setLatLng(latlng)
      .setContent(this.opts.buildClusterCard(vessels))
      .openOn(this.ctx.host.map);
  }

  /**
   * Flag the open card as having a lookup in flight — the spinning button and
   * the strip along its bottom edge (styled in `styles.scss`).
   *
   * Driven from the lookup rather than from the click delegate, so
   * the mark appears only when a request really starts (the guards there turn
   * back several clicks) and clears on every path out, including the ones that
   * do NOT replace the card: an operator with no data, or an error.
   *
   * Scoped to the hull whose card is open. The same lookup runs from the
   * Saved Vessels section's Refresh, where the open card — if there is one — may
   * belong to a different vessel and must not spin for someone else's request.
   */
  setVesselPopupUpdating(mmsi: string, on: boolean): void {
    if (this._vesselPopupMmsi !== String(mmsi ?? '').trim()) return;
    const card = this._vesselPopup?.getElement?.()?.querySelector?.('.ais-popup');
    // On the way out the card has usually been replaced already, and the fresh
    // one never carried the class — so this is simply a no-op there.
    card?.classList?.toggle('is-updating', on);
  }

  /**
   * Close the vessel card if it is describing THIS hull.
   *
   * Called by every path that takes a located vessel off the map. Scoped to the
   * one MMSI rather than calling `map.closePopup()`, because taking one vessel
   * down must not shut a card the user opened on another.
   */
  closeVesselPopupFor(mmsi: string): void {
    const key = String(mmsi ?? '').trim();
    if (!key || this._vesselPopupMmsi !== key) return;
    // `isOpen` guards the popup having been dismissed by a map click already —
    // Leaflet leaves the reference behind, and closing a closed popup is a no-op
    // only if it is still the map's current one.
    if (this._vesselPopup?.isOpen?.()) this.ctx.host.map?.closePopup?.(this._vesselPopup);
    this._vesselPopup = null;
    this._vesselPopupMmsi = null;
  }

  /**
   * Nulls the card references WITHOUT closing anything — for the task-switch
   * reset, which runs inside a shared sweep that has already taken the popup
   * off the map (invariant I1). A reference to the previous task's card must
   * not survive into the next one. The click-delegate target is deliberately
   * kept: the map container persists across task switches, and the delegate
   * with it.
   */
  dropRefs(): void {
    this._vesselPopup = null;
    this._vesselPopupMmsi = null;
  }
}
